import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
// 导入图片
import intelligentPlayImg from '../assets/images/features/intelligent-play.png';
import songLibraryImg from '../assets/images/features/song-library.png';
import progressTrackingImg from '../assets/images/features/progress-tracking.png';

const FeaturesContainer = styled.div`
  min-height: 100vh;
  background-color: #000;
  color: #fff;
`;

const Section = styled.section`
  padding: 80px 24px;
  
  @media (min-width: 768px) {
    padding: 120px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 60px 20px;
  }
`;

const HeroSection = styled(Section)`
  text-align: center;
  background: linear-gradient(to bottom, #000, #111);
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(120deg, #fff, #888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (min-width: 768px) {
    font-size: 48px;
    margin-bottom: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 20px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #888;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
  padding: 0 20px;
  
  @media (min-width: 768px) {
    font-size: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    padding: 0 12px;
  }
`;

const FeatureSection = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  gap: 80px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
    gap: 120px;
  }
  
  @media (max-width: 480px) {
    padding: 32px 12px;
    gap: 60px;
  }
`;

const Feature = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: center;
  width: 100%;
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.imageRight ? '1fr 1fr' : '1fr 1fr'};
    gap: 60px;
    padding: 0 20px;
  }
  
  @media (max-width: 480px) {
    gap: 24px;
    padding: 0 12px;
  }
`;

const FeatureContent = styled.div`
  order: ${props => props.imageRight ? 1 : 2};
  
  @media (min-width: 768px) {
    order: ${props => props.imageRight ? 1 : 1};
  }
`;

const FeatureImage = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  aspect-ratio: 16/9;
  order: ${props => props.imageRight ? 2 : 1};
  overflow: hidden;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
    background: rgba(255, 255, 255, 0.08);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 20px;
    transition: transform 0.3s ease;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    aspect-ratio: 4/3;
    border-radius: 16px;
    
    img {
      padding: 16px;
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
    
    img {
      padding: 12px;
    }
  }
`;

const FeatureTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #fff;
  
  @media (min-width: 768px) {
    font-size: 32px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  color: #888;
  line-height: 1.6;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin-bottom: 24px;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  font-size: 16px;
  color: #888;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:before {
    content: '•';
    color: #1890ff;
    margin-right: 12px;
    font-size: 20px;
  }
  
  @media (min-width: 768px) {
    font-size: 17px;
    margin-bottom: 16px;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

function Features() {
  return (
    <FeaturesContainer>
      <HeroSection>
        <Title>探索强大功能</Title>
        <Subtitle>
          从入门到精通，我们为您提供最专业的卡林巴学习体验
        </Subtitle>
      </HeroSection>

      <FeatureSection>
        <Feature imageRight>
          <FeatureContent imageRight>
            <FeatureTitle>智能跟弹系统</FeatureTitle>
            <FeatureDescription>
              创新的光标跟踪技术，让您的学习过程更加轻松自然
            </FeatureDescription>
            <FeatureList>
              <FeatureItem>实时光标指引，精准把握节奏</FeatureItem>
              <FeatureItem>智能速度调节，循序渐进学习</FeatureItem>
              <FeatureItem>自动进度跟踪，随时暂停继续</FeatureItem>
            </FeatureList>
          </FeatureContent>
          <FeatureImage>
            <img 
              src={intelligentPlayImg} 
              alt="智能跟弹系统界面展示"
              loading="lazy"
            />
          </FeatureImage>
        </Feature>

        <Feature imageRight>
          <FeatureContent imageRight>
            <FeatureTitle>自制曲目分享</FeatureTitle>
            <FeatureDescription>
              创作、分享、交流，打造属于卡林巴爱好者的音乐社区
            </FeatureDescription>
            <FeatureList>
              <FeatureItem>一键导入自制曲谱，分享创作灵感</FeatureItem>
              <FeatureItem>社区交流互动，共同探讨演奏技巧</FeatureItem>
              <FeatureItem>优质作品推荐，发现更多音乐可能</FeatureItem>
            </FeatureList>
          </FeatureContent>
          <FeatureImage>
            <img 
              src={progressTrackingImg} 
              alt="自制曲目分享界面"
              loading="lazy"
            />
          </FeatureImage>
        </Feature>

        <Feature imageRight>
          <FeatureContent imageRight>
            <FeatureTitle>官方曲库资源</FeatureTitle>
            <FeatureDescription>
              精心策划的曲目库，满足不同层次的学习需求
            </FeatureDescription>
            <FeatureList>
              <FeatureItem>经典名曲收录，提升品味</FeatureItem>
              <FeatureItem>流行歌曲改编，轻松上手</FeatureItem>
              <FeatureItem>难度分级系统，循序渐进</FeatureItem>
            </FeatureList>
          </FeatureContent>
          <FeatureImage imageRight>
            <img 
              src={songLibraryImg} 
              alt="曲库资源展示"
              loading="lazy"
            />
          </FeatureImage>
        </Feature>

      </FeatureSection>
    </FeaturesContainer>
  );
}

export default Features; 